import type {Status} from '@/modules/common/context/Consent/models';
import type {LooseString} from '@/types';

import Cookies from 'js-cookie';
import {useEffect, useState, useMemo} from 'react';

import {ConsentContext} from '@/modules/common/context/Consent/ConsentContext';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- interface needed for global declaration
  interface Window {
    CookieConsent?: {
      consent?: {
        stamp?: LooseString<'0'>; // 0 means no consent yet given
        marketing?: boolean;
        necessary?: boolean;
        preferences?: boolean;
        statistics?: boolean;
      };
    };
  }
}

// Reads the consent cookie to get the status
const getConsentStatus = (): Status | null => {
  // This information will be available after the user has made a decision and contains the consent status.
  const cookieConsent = window?.CookieConsent?.consent;
  const newCookieConsentCookie = Cookies.get('ucConsent');

  if (typeof cookieConsent?.stamp === 'string' && cookieConsent.stamp !== '0') {
    return {
      personalization: cookieConsent?.preferences ?? false,
      retargeting: cookieConsent.marketing ?? false,
      statistic: cookieConsent.statistics ?? false,
      technicalNecessary: cookieConsent.necessary ?? true,
    };
  }

  if (newCookieConsentCookie) {
    try {
      const c = Object.fromEntries(
        newCookieConsentCookie.split(',').map((s) => s.split(':'))
      );

      return {
        personalization: c.Personalisierung === 'true',
        retargeting: c.Retargeting === 'true',
        statistic: c.Statistik === 'true',
        technicalNecessary: c.Notwendig === 'true',
      };
    } catch {
      // If we could not read the cookie for whatever reason, we assume no consent was given
      return {
        personalization: false,
        retargeting: false,
        statistic: false,
        technicalNecessary: true,
      };
    }
  }

  // No cookie set yet
  return null;
};

const POLLING_INTERVAL = 250;
const TIMEOUT = 60_000;

type Props = React.PropsWithChildren;

export const ConsentProvider: React.FunctionComponent<Props> = ({children}) => {
  const [status, setStatus] = useState<Status | null>(() => getConsentStatus());

  useEffect(() => {
    let intervalId: number;
    let timeoutId: number;
    // Start polling until we know the consent status.
    // If it takes to long we stop checking and assume no consent.
    if (!status) {
      intervalId = window.setInterval(() => {
        const currentStatus = getConsentStatus();
        if (currentStatus) {
          setStatus(currentStatus);
          window.clearInterval(intervalId);
        }
      }, POLLING_INTERVAL);
      timeoutId = window.setTimeout(() => {
        window.clearInterval(intervalId);
      }, TIMEOUT);
    }
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [status]);

  const contextValue = useMemo(
    () => ({
      status,
    }),
    [status]
  );

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
    </ConsentContext.Provider>
  );
};
